import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, Phone, Globe, MapPin, Users as UsersIcon, FileText, Receipt,
  Sparkles, Send, CalendarPlus, CheckCircle2, Clock, AlertCircle, Download, Edit3,
  Activity, StickyNote, Calendar, CheckSquare, Plus, Loader2, TrendingUp, ChevronRight, Briefcase,
  Keyboard, FolderOpen, Eye, ClipboardList,
} from 'lucide-react';
import { sanitizeRichHtml } from '../lib/sanitize';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import CompanyFormDialog from '../components/CompanyFormDialog';
import GroupedDocDialog from '../components/GroupedDocDialog';
import CompanyEmailsTab from '../components/CompanyEmailsTab';
import CompanyQuestionnairesTab from '../components/CompanyQuestionnairesTab';
import {
  useKeyboardShortcuts, ShortcutsOverlay, HeaderActionMenu, InlineEditField,
  ExpressLearnerForm, QuickTaskTemplates, parseSlashCommand, useSmartPasteWatcher,
  pushRecentCompany, RecentCompaniesNav, DocumentsTab, EmailComposer,
} from '../components/CompanyProductivity';

const ACTIVITY_META = {
  note:    { icon: StickyNote, color: '#4F46E5', label: 'Note', bg: '#EEF2FF' },
  call:    { icon: Phone,      color: '#14B8A6', label: 'Appel', bg: '#E6FBF7' },
  email:   { icon: Mail,       color: '#3B82F6', label: 'Email', bg: '#E0F2FE' },
  meeting: { icon: Calendar,   color: '#F59E0B', label: 'RDV', bg: '#FEF3E7' },
  task:    { icon: CheckSquare,color: '#EC4899', label: 'Tâche', bg: '#FDF4FF' },
};

const STAGE_COLORS = { 'Nouveau': '#8E8FA5', 'Qualification': '#3B82F6', 'Proposition': '#F59E0B', 'Négociation': '#4F46E5', 'Gagné': '#14B8A6', 'Perdu': '#EF4444' };

const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

function relativeTime(d) {
  if (!d) return '—';
  const date = new Date(d);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return date.toLocaleDateString('fr-FR');
}

const TABS = [
  { key: 'overview',       label: 'Synthèse',        icon: TrendingUp },
  { key: 'crm',            label: 'Suivi CRM',       icon: Activity },
  { key: 'emails',         label: 'Emails',          icon: Mail },
  { key: 'learners',       label: 'Apprenants',      icon: UsersIcon },
  { key: 'billing',        label: 'Devis & Factures', icon: Receipt },
  { key: 'preinscriptions', label: 'Pré-inscriptions', icon: CalendarPlus },
  { key: 'conventions',    label: 'Conventions',     icon: FileText },
  { key: 'convocations',   label: 'Convocations',    icon: Send },
  { key: 'documents',      label: 'Documents',       icon: FolderOpen },
  { key: 'questionnaires', label: 'Questionnaires',  icon: ClipboardList },
];

/* Vague 96.4 — small "👁 Vu" pill rendered next to a quote in the company
   detail view when the recipient has opened the email containing the quote.
   Data is fetched from /api/email-opens/quotes (aggregated tracking-pixel hits). */
function _relativeShort(d) {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 30) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(d).toLocaleDateString('fr-FR');
}

function QuoteOpenedPill({ stats, testId }) {
  if (!stats || !stats.last_opened_at || !stats.open_count) return null;
  const count = stats.open_count;
  const label = count === 1 ? 'Vu' : `Vu ×${count}`;
  return (
    <span
      data-testid={testId}
      title={`Email ouvert ${count} fois${stats.first_opened_at ? ` — première ouverture ${new Date(stats.first_opened_at).toLocaleString('fr-FR')}` : ''}\nDernière : ${new Date(stats.last_opened_at).toLocaleString('fr-FR')}`}
      className="inline-flex items-center gap-1 px-1.5 h-5 rounded-md bg-[#DBEAFE] text-[#1D4ED8] text-[10px] font-bold whitespace-nowrap"
    >
      <Eye className="w-2.5 h-2.5" /> {label} · {_relativeShort(stats.last_opened_at)}
    </span>
  );
}

export default function EntrepriseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [grouped, setGrouped] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [learners, setLearners] = useState([]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [activitySeed, setActivitySeed] = useState(null); // { type } prefilled by shortcut
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailReplyTo, setEmailReplyTo] = useState(null);
  const [emailOpens, setEmailOpens] = useState({}); // {quote_id: {open_count, last_opened_at, ...}}
  const titleInputRef = useRef(null); // focused via "/"

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${id}/dashboard`);
      setData(data);
      setLearners(data.learners || []);
      pushRecentCompany(data.company);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Fiche introuvable', variant: 'destructive' });
      navigate('/app/entreprises');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { reload(); }, [reload]);

  // Vague 96.4 — fetch email-opens map (tracking-pixel hits per quote_id)
  useEffect(() => {
    api.get('/email-opens/quotes').then(({ data }) => setEmailOpens(data || {})).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (tab !== 'crm') return;
    setActivitiesLoading(true);
    api.get(`/companies/${id}/activities`).then(({ data }) => setActivities(data || []))
      .catch(() => setActivities([]))
      .finally(() => setActivitiesLoading(false));
  }, [tab, id]);

  // Load full learners list when needed
  useEffect(() => {
    if (tab !== 'learners' && tab !== 'billing') return;
    api.get(`/companies/${id}/learners`).then(({ data }) => setLearners(data || [])).catch(() => {});
  }, [tab, id]);

  // ============ Inline edit save ============
  const saveField = useCallback(async (field, val) => {
    await api.put(`/companies/${id}`, { [field]: val });
    setData((p) => p ? ({ ...p, company: { ...p.company, [field]: val } }) : p);
    toast({ title: 'Mis à jour ✓' });
  }, [id]);

  // ============ Quick action triggers ============
  const triggerQuick = useCallback((kind) => {
    if (kind === 'email') {
      setEmailReplyTo(null);
      setEmailOpen(true);
      return;
    }
    if (['note', 'task', 'call', 'meeting'].includes(kind)) {
      setTab('crm');
      setActivitySeed({ type: kind, ts: Date.now() });
    } else if (kind === 'quote') {
      setTab('billing');
      navigate('/app/devis', { state: { prefillCreate: { company_id: id, learner_ids: learners.map((l) => l.id) } } });
    } else if (kind === 'invoice') {
      setTab('billing');
      setGrouped({ kind: 'invoice', amount_per_learner: 0, description: '', due_date: '' });
    } else if (kind === 'learner') {
      setTab('learners');
      // Trigger express form auto-open via custom event
      setTimeout(() => document.querySelector('[data-testid="express-learner-open"]')?.click(), 50);
    }
  }, [id, learners, navigate]);

  // ============ Keyboard shortcuts ============
  useKeyboardShortcuts({
    'n': () => triggerQuick('note'),
    't': () => triggerQuick('task'),
    'c': () => triggerQuick('call'),
    'e': () => triggerQuick('email'),
    'r': () => triggerQuick('meeting'),
    'd': () => triggerQuick('quote'),
    'f': () => triggerQuick('invoice'),
    'a': () => triggerQuick('learner'),
    '?': () => setShortcutsOpen((v) => !v),
    '/': () => { setTab('crm'); setTimeout(() => titleInputRef.current?.focus(), 50); },
    'esc': () => { setShortcutsOpen(false); setEditing(false); setGrouped(null); setEmailOpen(false); },
    'j': () => navigateRecent(-1),
    'k': () => navigateRecent(1),
  }, [triggerQuick]);

  const navigateRecent = (dir) => {
    try {
      const list = JSON.parse(localStorage.getItem('ifaap_recent_companies') || '[]');
      if (list.length < 2) return;
      const idx = list.findIndex((x) => x.id === id);
      const next = list[(idx + dir + list.length) % list.length];
      if (next && next.id !== id) navigate(`/app/entreprises/${next.id}`);
    } catch { /* ignore */ }
  };

  // ============ Smart paste detection (header context) ============
  useSmartPasteWatcher(
    (r) => toast({ title: 'SIRET/SIREN détecté', description: `${r.value} — ouvrez "Modifier la fiche" puis collez dans le champ SIRET pour récupérer les infos SIRENE.` }),
    (r) => toast({ title: 'Email détecté', description: `${r.value} — utilisez la création express d'apprenant (touche A).` }),
  );

  if (loading || !data) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>;
  }

  const c = data.company;
  const k = data.kpis;
  const initials = (c.name || '?').split(' ').slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();

  return (
    <div className="space-y-4" data-testid="entreprise-detail-page">
      {/* Breadcrumb + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[13px] text-[#6B6C85]">
          <Link to="/app/entreprises" className="hover:text-[#4F46E5] flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Entreprises</Link>
          <ChevronRight className="w-3.5 h-3.5 text-[#8E8FA5]" />
          <span className="text-[#1A1B2E] font-semibold">{c.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="shortcuts-open-btn" onClick={() => setShortcutsOpen(true)}
            className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE] hover:text-[#4F46E5] flex items-center gap-1.5"
            title="Raccourcis clavier (?)"
          >
            <Keyboard className="w-3.5 h-3.5" /> Raccourcis
          </button>
          <button data-testid="company-edit-btn" onClick={() => setEditing(true)} className="h-9 px-3.5 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE] hover:text-[#4F46E5] flex items-center gap-1.5">
            <Edit3 className="w-3.5 h-3.5" /> Modifier la fiche
          </button>
        </div>
      </div>

      {/* Recent companies pill bar */}
      <RecentCompaniesNav currentId={id} />

      {/* HEADER CARD */}
      <div className="soft-card p-6">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-2xl brand-gradient text-white flex items-center justify-center text-[22px] font-bold shrink-0" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {initials || <Building2 className="w-7 h-7" />}
          </div>
          <div className="flex-1 min-w-[280px]">
            <h1 className="text-[24px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="company-name">{c.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[#6B6C85] mt-1.5">
              {c.siret && <span className="font-mono">SIRET {c.siret}</span>}
              {c.siren && !c.siret && <span className="font-mono">SIREN {c.siren}</span>}
              {c.legal_form && <span>· {c.legal_form}</span>}
              {c.naf_code && <span>· NAF {c.naf_code}</span>}
              {c.opco_code && <span>· OPCO {c.opco_code}</span>}
            </div>
            {/* Inline-editable contact row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-[12.5px]">
              <InlineEditField
                value={c.contact_name}
                placeholder="Contact"
                icon={Briefcase}
                testId="inline-contact-name"
                onSave={(v) => saveField('contact_name', v)}
              />
              <InlineEditField
                value={c.contact_email}
                placeholder="Email contact"
                type="email"
                icon={Mail}
                testId="inline-contact-email"
                onSave={(v) => saveField('contact_email', v)}
              />
              <InlineEditField
                value={c.contact_phone}
                placeholder="Téléphone"
                type="tel"
                icon={Phone}
                testId="inline-contact-phone"
                onSave={(v) => saveField('contact_phone', v)}
              />
              {c.website && <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#3B82F6] hover:underline"><Globe className="w-3.5 h-3.5" /> {c.website}</a>}
              {(c.address || c.city) && <span className="inline-flex items-center gap-1.5 text-[#6B6C85]"><MapPin className="w-3.5 h-3.5" /> {[c.address, c.postal_code, c.city].filter(Boolean).join(', ')}</span>}
            </div>
          </div>
          {/* Quick action menu — Vague 95 (right of the header) */}
          <div className="shrink-0">
            <HeaderActionMenu onAction={triggerQuick} />
          </div>
        </div>

        {/* KPIs row */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="company-kpis">
          <KpiCard label="CA total" value={fmtEur(k.ca_total)} color="#4F46E5" />
          <KpiCard label="Encaissé" value={fmtEur(k.ca_paid)} color="#0F766E" />
          <KpiCard label="En retard" value={fmtEur(k.ca_overdue)} color={k.ca_overdue > 0 ? '#EF4444' : '#8E8FA5'} alert={k.ca_overdue > 0} />
          <KpiCard label="Apprenants" value={k.learners_count} color="#3B82F6" />
          <KpiCard label="Sessions" value={k.sessions_count} color="#F59E0B" />
          <KpiCard label="Tâches dues" value={k.tasks_due_count} color={k.tasks_overdue_count > 0 ? '#EF4444' : '#4F46E5'} alert={k.tasks_overdue_count > 0} hint={k.tasks_overdue_count > 0 ? `${k.tasks_overdue_count} en retard` : null} />
        </div>

        {/* Quick task templates */}
        <div className="mt-4">
          <QuickTaskTemplates companyId={id} onCreated={(a) => { setActivities((p) => [a, ...p]); reload(); }} />
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center gap-1 border-b border-[#EFF0F6] overflow-x-auto" data-testid="company-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`company-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 h-11 text-[13px] font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              tab === t.key ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            {t.key === 'crm' && k.tasks_due_count > 0 && (
              <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${k.tasks_overdue_count > 0 ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-[#EEF2FF] text-[#4F46E5]'}`}>
                {k.tasks_due_count}
              </span>
            )}
            {t.key === 'learners' && <span className="text-[10.5px] font-bold text-[#8E8FA5]">{k.learners_count}</span>}
            {t.key === 'preinscriptions' && (c.preinscriptions || []).length > 0 && <span className="text-[10.5px] font-bold text-[#8E8FA5]">{c.preinscriptions.length}</span>}
            {t.key === 'conventions' && (c.signed_conventions || []).length > 0 && <span className="text-[10.5px] font-bold text-[#0F766E]">{c.signed_conventions.length}</span>}
            {t.key === 'convocations' && (c.sent_convocations || []).length > 0 && <span className="text-[10.5px] font-bold text-[#0F766E]">{c.sent_convocations.length}</span>}
            {t.key === 'documents' && (c.documents || []).length > 0 && <span className="text-[10.5px] font-bold text-[#4F46E5]">{c.documents.length}</span>}
          </button>
        ))}
      </div>

      {/* TAB CONTENT */}
      {tab === 'overview' && <OverviewTab data={data} onTabChange={setTab} emailOpens={emailOpens} />}
      {tab === 'crm' && <CrmTab companyId={id} activities={activities} loading={activitiesLoading} deals={data.deals} onAdded={(a) => setActivities((prev) => [a, ...prev])} seed={activitySeed} titleInputRef={titleInputRef} onReply={(a) => { setEmailReplyTo(a); setEmailOpen(true); }} />}
      {tab === 'emails' && <CompanyEmailsTab companyId={id} navigate={navigate} />}
      {tab === 'learners' && <LearnersTab companyId={id} companyName={c.name} learners={learners} setLearners={setLearners} />}
      {tab === 'billing' && <BillingTab data={data} learners={learners} emailOpens={emailOpens} onCreateGrouped={() => setGrouped({ kind: 'invoice', amount_per_learner: 0, description: '', due_date: '' })} onCreateQuote={() => navigate('/app/devis', { state: { prefillCreate: { company_id: id, learner_ids: learners.map((l) => l.id) } } })} />}
      {tab === 'preinscriptions' && <PreinscriptionsTab company={c} navigate={navigate} />}
      {tab === 'conventions' && <ConventionsTab company={c} navigate={navigate} />}
      {tab === 'convocations' && <ConvocationsTab company={c} navigate={navigate} />}
      {tab === 'documents' && <DocumentsTab companyId={id} />}
      {tab === 'questionnaires' && <CompanyQuestionnairesTab companyId={id} companyName={c?.name} />}

      {/* Edit dialog */}
      <CompanyFormDialog
        key={`edit-${c.id}`}
        open={editing}
        onOpenChange={setEditing}
        initialValues={c}
        onSubmit={async (payload) => {
          const clean = { ...payload };
          delete clean.id; delete clean.org_id; delete clean.created_at;
          delete clean.preinscriptions; delete clean.signed_conventions;
          delete clean.sent_convocations; delete clean.documents;
          await api.put(`/companies/${c.id}`, clean);
          toast({ title: 'Fiche mise à jour ✓' });
          reload();
        }}
      />

      {/* Grouped invoice dialog */}
      {grouped && (
        <GroupedDocDialog
          open={!!grouped}
          kind={grouped.kind}
          onClose={() => setGrouped(null)}
          company={c}
          learners={learners}
          initial={grouped}
          onCreated={(doc) => { setGrouped(null); reload(); toast({ title: 'Facture groupée créée', description: `${doc.id} · ${Number(doc.amount).toLocaleString('fr-FR')} €` }); }}
        />
      )}

      {/* Shortcuts cheatsheet overlay */}
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Email composer (Vague 95) */}
      <EmailComposer
        open={emailOpen}
        onClose={() => { setEmailOpen(false); setEmailReplyTo(null); }}
        company={c}
        defaultTo={c.contact_email || ''}
        replyTo={emailReplyTo}
        onSent={(activity) => {
          if (activity) setActivities((prev) => [activity, ...prev]);
          reload();
        }}
      />
    </div>
  );
}

function KpiCard({ label, value, color = '#4F46E5', hint, alert }) {
  return (
    <div className={`rounded-xl border p-3 ${alert ? 'border-[#FECACA] bg-[#FEF2F2]' : 'border-[#EFF0F6] bg-white'}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6B6C85]">{label}</div>
      <div className="text-[18px] font-bold mt-0.5 leading-tight" style={{ color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</div>
      {hint && <div className="text-[10.5px] mt-0.5" style={{ color }}>{hint}</div>}
    </div>
  );
}

/* ==================== Overview tab ==================== */
function OverviewTab({ data, onTabChange, emailOpens = {} }) {
  const u = data.upcoming;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Recent CRM activity */}
      <div className="soft-card p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dernières activités CRM</h3>
          <button onClick={() => onTabChange('crm')} className="text-[12px] text-[#4F46E5] font-semibold hover:underline">Tout voir →</button>
        </div>
        {(data.recent_activities || []).length === 0 ? (
          <div className="text-center py-6 text-[12.5px] text-[#8E8FA5] italic">Aucune activité enregistrée pour cette entreprise.</div>
        ) : (
          <div className="space-y-2">
            {(data.recent_activities || []).slice(0, 5).map((a) => {
              const m = ACTIVITY_META[a.type] || ACTIVITY_META.note;
              const Ic = m.icon;
              return (
                <div key={a.id} className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-[#FAFAFE]">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.bg }}>
                    <Ic className="w-4 h-4" style={{ color: m.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1A1B2E]">{a.title}</div>
                    {a.content && <div className="text-[12px] text-[#6B6C85] mt-0.5 line-clamp-2">{a.content}</div>}
                    <div className="text-[10.5px] text-[#8E8FA5] mt-0.5">{relativeTime(a.created_at)} · {a.created_by_name || a.created_by}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upcoming deadlines */}
      <div className="soft-card p-5">
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Prochaines échéances</h3>
        {(u.tasks_overdue.length === 0 && u.tasks_upcoming.length === 0 && u.invoices_overdue.length === 0 && u.invoices_upcoming.length === 0) ? (
          <div className="text-center py-6 text-[12.5px] text-[#8E8FA5] italic">Rien d'urgent ✓</div>
        ) : (
          <div className="space-y-2">
            {u.tasks_overdue.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#FEF2F2] border border-[#FECACA]">
                <CheckSquare className="w-3.5 h-3.5 text-[#B91C1C] shrink-0" />
                <span className="text-[12px] font-semibold text-[#1A1B2E] flex-1 truncate">{t.title}</span>
                <span className="text-[10.5px] text-[#B91C1C] font-bold">En retard {fmtDate(t.due_date)}</span>
              </div>
            ))}
            {u.tasks_upcoming.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#FEF3E7] border border-[#FCD9A6]">
                <CheckSquare className="w-3.5 h-3.5 text-[#B45309] shrink-0" />
                <span className="text-[12px] font-semibold text-[#1A1B2E] flex-1 truncate">{t.title}</span>
                <span className="text-[10.5px] text-[#B45309]">{fmtDate(t.due_date)}</span>
              </div>
            ))}
            {u.invoices_overdue.map((i) => (
              <div key={i.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#FEF2F2] border border-[#FECACA]">
                <Receipt className="w-3.5 h-3.5 text-[#B91C1C] shrink-0" />
                <span className="text-[12px] font-semibold text-[#1A1B2E] flex-1 truncate">{i.id}</span>
                <span className="text-[10.5px] text-[#B91C1C] font-bold">{fmtEur(i.amount)} · {fmtDate(i.due_date)}</span>
              </div>
            ))}
            {u.invoices_upcoming.map((i) => (
              <div key={i.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#FEF3E7] border border-[#FCD9A6]">
                <Receipt className="w-3.5 h-3.5 text-[#B45309] shrink-0" />
                <span className="text-[12px] font-semibold text-[#1A1B2E] flex-1 truncate">{i.id}</span>
                <span className="text-[10.5px] text-[#B45309]">{fmtEur(i.amount)} · {fmtDate(i.due_date)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent quotes */}
      <div className="soft-card p-5">
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Derniers devis</h3>
        {data.recent_quotes.length === 0 ? <div className="text-[12.5px] text-[#8E8FA5] italic">Aucun.</div> : (
          <div className="space-y-1.5">
            {data.recent_quotes.map((q) => (
              <div key={q.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FAFAFE]">
                <FileText className="w-3.5 h-3.5 text-[#4F46E5]" />
                <span className="font-mono text-[11.5px] text-[#1A1B2E]">{q.id}</span>
                <QuoteOpenedPill stats={emailOpens[q.id]} testId={`overview-quote-opened-${q.id}`} />
                <span className="text-[11px] text-[#8E8FA5] flex-1">{q.date}</span>
                <span className="text-[12px] font-semibold text-[#1A1B2E]">{fmtEur(q.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent invoices */}
      <div className="soft-card p-5 lg:col-span-2">
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dernières factures</h3>
        {data.recent_invoices.length === 0 ? <div className="text-[12.5px] text-[#8E8FA5] italic">Aucune.</div> : (
          <div className="space-y-1.5">
            {data.recent_invoices.map((i) => (
              <div key={i.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FAFAFE]">
                <Receipt className="w-3.5 h-3.5 text-[#0F766E]" />
                <span className="font-mono text-[11.5px] text-[#1A1B2E]">{i.id}</span>
                <span className="text-[11px] text-[#8E8FA5]">Éch. {i.dueDate || i.date}</span>
                <span className="ml-auto text-[12px] font-semibold text-[#1A1B2E]">{fmtEur(i.amount)}</span>
                <StatusBadge status={i.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = {
    'Payée':      { icon: CheckCircle2, style: 'bg-[#E6FBF7] text-[#0F766E]' },
    'En attente': { icon: Clock,        style: 'bg-[#FEF3E7] text-[#B45309]' },
    'En retard':  { icon: AlertCircle,  style: 'bg-[#FEF2F2] text-[#B91C1C]' },
  };
  const cur = m[status] || { icon: Clock, style: 'bg-[#F3F4F8] text-[#4A4B63]' };
  const Ic = cur.icon;
  return <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${cur.style}`}><Ic className="w-3 h-3" /> {status || '—'}</span>;
}

/* ==================== CRM Tab ==================== */
function CrmTab({ companyId, activities, loading, deals, onAdded, seed, titleInputRef, onReply }) {
  const [type, setType] = useState('note');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Apply seed (from keyboard shortcut / FAB) to switch type and focus title
  useEffect(() => {
    if (seed?.type) {
      setType(seed.type);
      setTimeout(() => titleInputRef?.current?.focus(), 50);
    }
  }, [seed, titleInputRef]);

  // Slash-command auto-detection on title input
  const handleTitleChange = (e) => {
    const v = e.target.value;
    const parsed = parseSlashCommand(v);
    if (parsed) {
      // Replace the input value with the cleaned title (verb stripped, suffix stripped)
      setType(parsed.type);
      setTitle(parsed.title);
      if (parsed.due) setDueDate(parsed.due);
      toast({ title: `Type basculé : ${parsed.type}`, description: parsed.due ? `Échéance : ${parsed.due}` : undefined });
      return;
    }
    setTitle(v);
  };

  const submit = async () => {
    if (!title.trim()) { toast({ title: 'Titre requis', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const includeDue = (type === 'task' || type === 'meeting');
      const { data } = await api.post(`/companies/${companyId}/activities`, {
        type, title: title.trim(), content: content.trim(), due_date: includeDue ? (dueDate || null) : null,
      });
      onAdded(data);
      setTitle(''); setContent(''); setDueDate('');
      toast({ title: 'Activité ajoutée' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
      {/* Compact Timeline (Vague 93) */}
      <CompactTimeline
        activities={activities}
        loading={loading}
        onReply={onReply}
      />

      {/* Sidebar : new activity + deals */}
      <div className="space-y-4">
        <div className="soft-card p-5">
          <h3 className="text-[14px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Nouvelle activité</h3>
          <div className="grid grid-cols-5 gap-1.5 mb-3">
            {Object.entries(ACTIVITY_META).map(([k, m]) => {
              const Ic = m.icon;
              const sel = type === k;
              return (
                <button
                  key={k}
                  data-testid={`activity-type-${k}`}
                  onClick={() => setType(k)}
                  className={`h-12 rounded-lg flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${sel ? 'border-2' : 'border border-[#EFF0F6] bg-white'}`}
                  style={sel ? { borderColor: m.color, background: m.bg, color: m.color } : { color: '#6B6C85' }}
                >
                  <Ic className="w-3.5 h-3.5" />
                  {m.label}
                </button>
              );
            })}
          </div>
          <input data-testid="activity-title" ref={titleInputRef} value={title} onChange={handleTitleChange} placeholder="Titre · ou /task /call /email +7j" className="w-full h-9 px-3 rounded-lg border border-[#EFF0F6] text-[13px] mb-2 focus:border-[#4F46E5] outline-none" onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }} />
          <textarea data-testid="activity-content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Contenu (facultatif)" rows={3} className="w-full px-3 py-2 rounded-lg border border-[#EFF0F6] text-[12.5px] mb-2 resize-none focus:border-[#4F46E5] outline-none" />
          {(type === 'task' || type === 'meeting') && (
            <input type="date" data-testid="activity-due" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full h-9 px-3 rounded-lg border border-[#EFF0F6] text-[13px] mb-2 focus:border-[#4F46E5] outline-none" />
          )}
          <button data-testid="activity-submit" onClick={submit} disabled={submitting || !title.trim()} className="w-full h-10 rounded-lg brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>

        <div className="soft-card p-5">
          <h3 className="text-[14px] font-semibold text-[#1A1B2E] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Opportunités liées</h3>
          {deals.length === 0 ? (
            <div className="text-[12px] text-[#8E8FA5] italic">Aucune opportunité CRM rattachée.</div>
          ) : (
            <div className="space-y-1.5">
              {deals.map((d) => (
                <Link key={d.id} to={`/app/crm`} className="block p-2 rounded-lg border border-[#EFF0F6] hover:border-[#C7D2FE] hover:bg-[#FAFAFE]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[d.stage] || '#8E8FA5' }} />
                    <span className="text-[12px] font-semibold text-[#1A1B2E] flex-1 truncate">{d.contact || d.id}</span>
                    <span className="text-[12px] font-bold text-[#4F46E5]">{fmtEur(d.value)}</span>
                  </div>
                  <div className="text-[10.5px] text-[#8E8FA5] mt-0.5">{d.stage} · {d.probability || 0}%</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== Compact Timeline (Vague 93) ==================== */
const TYPE_FILTERS = [
  { key: 'all',     label: 'Tous',    color: '#6B6C85' },
  { key: 'note',    label: 'Notes',   color: '#4F46E5' },
  { key: 'task',    label: 'Tâches',  color: '#EC4899' },
  { key: 'call',    label: 'Appels',  color: '#14B8A6' },
  { key: 'email',   label: 'Emails',  color: '#3B82F6' },
  { key: 'meeting', label: 'RDV',     color: '#F59E0B' },
];

function dayBucket(d) {
  if (!d) return 'Sans date';
  const date = new Date(d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest = new Date(today.getTime() - 86400000);
  const week = new Date(today.getTime() - 7 * 86400000);
  date.setHours(0, 0, 0, 0);
  if (date.getTime() === today.getTime()) return "Aujourd'hui";
  if (date.getTime() === yest.getTime()) return "Hier";
  if (date.getTime() >= week.getTime()) return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

function CompactTimeline({ activities, loading, onReply }) {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const filtered = filter === 'all' ? activities : activities.filter((a) => a.type === filter);
  const counts = activities.reduce((acc, a) => { acc[a.type] = (acc[a.type] || 0) + 1; return acc; }, {});

  // Group by day bucket (preserves order — already sorted desc)
  const groups = [];
  let curBucket = null;
  filtered.forEach((a) => {
    const b = dayBucket(a.created_at);
    if (!curBucket || curBucket.label !== b) {
      curBucket = { label: b, items: [] };
      groups.push(curBucket);
    }
    curBucket.items.push(a);
  });

  return (
    <div className="soft-card p-5" data-testid="compact-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Timeline d'activité</h3>
          <span className="text-[11px] text-[#8E8FA5]">({activities.length} au total)</span>
        </div>
        {/* Type filters */}
        <div className="flex items-center gap-1 bg-[#F6F7FB] rounded-lg p-0.5 flex-wrap">
          {TYPE_FILTERS.map((f) => {
            const sel = filter === f.key;
            const n = f.key === 'all' ? activities.length : (counts[f.key] || 0);
            return (
              <button
                key={f.key}
                data-testid={`tl-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`h-7 px-2.5 rounded-md text-[11.5px] font-semibold transition-colors flex items-center gap-1 ${sel ? 'bg-white shadow-sm' : 'text-[#6B6C85] hover:text-[#1A1B2E]'}`}
                style={sel ? { color: f.color } : undefined}
              >
                {f.label} <span className="text-[10px] opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline text-[#4F46E5]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-[12.5px] text-[#8E8FA5] italic">
          {activities.length === 0 ? "Aucune activité enregistrée. Ajoutez une note, un appel ou une tâche →" : "Aucune activité de ce type."}
        </div>
      ) : (
        <div className="relative">
          {/* Vertical timeline rail */}
          <div className="absolute left-[14px] top-2 bottom-2 w-px bg-gradient-to-b from-[#EFF0F6] via-[#EFF0F6] to-transparent" />
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="sticky top-0 z-[2] flex items-center gap-2 py-1 bg-white">
                  <div className="w-7 h-7 rounded-full bg-[#EEF2FF] border-2 border-white shadow-sm flex items-center justify-center text-[9.5px] font-bold uppercase text-[#4F46E5]">
                    {g.items.length}
                  </div>
                  <span className="text-[11.5px] font-bold uppercase tracking-wide text-[#4A4B63] capitalize">{g.label}</span>
                </div>
                <div className="ml-7 space-y-1 mt-1.5">
                  {g.items.map((a) => {
                    const m = ACTIVITY_META[a.type] || ACTIVITY_META.note;
                    const Ic = m.icon;
                    const overdue = a.type === 'task' && !a.completed && a.due_date && new Date(a.due_date) < new Date();
                    const isOpen = expanded.has(a.id);
                    const hasMore = !!a.content || a.created_by_name;
                    // Email status (Vague 96 Phase 2)
                    const meta = a.metadata || {};
                    const isEmail = a.type === 'email';
                    const emailSent = isEmail && meta.sent === true;
                    const emailFailed = isEmail && meta.sent === false;
                    const emailOpened = isEmail && meta.opened_at;
                    const emailOpenCount = meta.open_count || 0;
                    return (
                      <div key={a.id} data-testid={`activity-${a.id}`}
                        className={`group flex items-start gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-[#FAFAFE] transition-colors`}
                        onClick={() => hasMore && toggle(a.id)}
                      >
                        <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: m.bg }}>
                          <Ic className="w-3 h-3" style={{ color: m.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap leading-tight">
                            <span className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{a.title}</span>
                            <span className="text-[10px] text-[#8E8FA5] shrink-0">
                              {new Date(a.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {a.prospect && <span className="text-[9.5px] bg-[#EEF2FF] text-[#4F46E5] px-1 rounded font-semibold">Deal</span>}
                            {overdue && <span className="text-[9.5px] bg-[#FEE2E2] text-[#B91C1C] px-1 rounded font-semibold">En retard</span>}
                            {a.type === 'task' && a.due_date && !overdue && <span className="text-[9.5px] text-[#B45309]">⏰ {fmtDate(a.due_date)}</span>}
                            {/* Email status badges */}
                            {emailSent && <span data-testid={`email-status-sent-${a.id}`} className="text-[9.5px] bg-[#D1FAE5] text-[#047857] px-1 rounded font-semibold">✓ Envoyé</span>}
                            {emailFailed && <span data-testid={`email-status-failed-${a.id}`} className="text-[9.5px] bg-[#FEE2E2] text-[#B91C1C] px-1 rounded font-semibold">✗ Échec</span>}
                            {emailOpened && <span data-testid={`email-status-opened-${a.id}`} className="text-[9.5px] bg-[#DBEAFE] text-[#1D4ED8] px-1 rounded font-semibold">👁 Ouvert{emailOpenCount > 1 ? ` ×${emailOpenCount}` : ''}</span>}
                            {isEmail && meta.attachments_count > 0 && <span className="text-[9.5px] text-[#6B6C85]">📎 {meta.attachments_count}</span>}
                          </div>
                          {isOpen && hasMore && (
                            <div className="mt-1 pl-0.5 text-[11.5px] text-[#4A4B63]">
                              {a.content && <div className="whitespace-pre-wrap">{a.content}</div>}
                              {/* Vague 96.9 — render full email HTML body (incl. signature image) on expand */}
                              {isEmail && meta.body_html_full && (
                                <div data-testid={`email-body-html-${a.id}`}
                                  className="mt-2 p-2 bg-white border border-dashed border-[#EFF0F6] rounded max-h-[280px] overflow-y-auto [&_img]:max-w-full [&_img]:h-auto"
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(meta.body_html_full) }} />
                              )}
                              <div className="text-[10px] text-[#8E8FA5] mt-0.5 flex items-center gap-2">
                                <span>par {a.created_by_name || a.created_by || '—'}</span>
                                {meta.signature_embedded && <span className="text-[#0F766E]">· signature des paramètres</span>}
                                {isEmail && onReply && (
                                  <button data-testid={`email-reply-${a.id}`}
                                    onClick={(ev) => { ev.stopPropagation(); onReply(a); }}
                                    className="text-[10.5px] font-semibold text-[#4F46E5] hover:underline flex items-center gap-1">
                                    <Send className="w-3 h-3" /> Répondre
                                  </button>
                                )}
                                {emailFailed && meta.error && (
                                  <span className="text-[#B91C1C] italic">— {meta.error.slice(0, 80)}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {hasMore && (
                          <ChevronRight className={`w-3 h-3 mt-1 text-[#8E8FA5] transition-transform shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== Learners Tab ==================== */
function LearnersTab({ companyId, companyName, learners, setLearners }) {
  return (
    <div className="space-y-3">
      <ExpressLearnerForm
        companyId={companyId}
        companyName={companyName}
        onCreated={(l) => setLearners((prev) => [l, ...prev])}
      />
      <div className="soft-card p-5">
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Apprenants rattachés ({learners.length})</h3>
        {learners.length === 0 ? (
          <div className="text-[13px] text-[#8E8FA5] italic">Aucun apprenant rattaché.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {learners.map((l) => (
              <div key={l.id} data-testid={`learner-${l.id}`} className="flex items-center gap-2 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6]">
                <div className="w-10 h-10 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[13px] shrink-0">
                  {(l.firstName?.[0] || '')}{(l.lastName?.[0] || '')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{l.firstName} {l.lastName}</div>
                  <div className="text-[11.5px] text-[#8E8FA5] truncate">{l.email}</div>
                </div>
                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">{l.status || 'Actif'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== Billing Tab ==================== */
function BillingTab({ data, learners, onCreateGrouped, onCreateQuote, emailOpens = {} }) {
  const k = data.kpis;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-4 bg-[#E6FBF7] border border-[#A7F3D0]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#0F766E]">Encaissé</div>
          <div className="text-[20px] font-bold text-[#0F766E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-paid">{fmtEur(k.ca_paid)}</div>
        </div>
        <div className="rounded-xl p-4 bg-[#FEF3E7] border border-[#FDE68A]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#B45309]">En attente</div>
          <div className="text-[20px] font-bold text-[#B45309] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-pending">{fmtEur(k.ca_pending)}</div>
        </div>
        <div className="rounded-xl p-4 bg-[#FEF2F2] border border-[#FECACA]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#B91C1C]">En retard</div>
          <div className="text-[20px] font-bold text-[#B91C1C] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-overdue">{fmtEur(k.ca_overdue)}</div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button data-testid="billing-create-quote-btn" disabled={!learners.length} onClick={onCreateQuote} className="h-9 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50">
          <FileText className="w-3.5 h-3.5" /> Créer un devis
        </button>
        <button data-testid="billing-create-grouped-btn" disabled={!learners.length} onClick={onCreateGrouped} className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <Sparkles className="w-3.5 h-3.5" /> Facture groupée
        </button>
      </div>
      {/* Quotes */}
      <div className="soft-card p-5">
        <h4 className="text-[13.5px] font-semibold text-[#1A1B2E] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Devis ({k.quotes_count})</h4>
        {data.recent_quotes.length === 0 ? <div className="text-[12.5px] italic text-[#8E8FA5]">Aucun.</div> : (
          <div className="space-y-1.5">
            {data.recent_quotes.map((q) => (
              <div key={q.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE]">
                <FileText className="w-3.5 h-3.5 text-[#4F46E5]" />
                <span className="font-mono text-[12px] text-[#1A1B2E]">{q.id}</span>
                <QuoteOpenedPill stats={emailOpens[q.id]} testId={`billing-quote-opened-${q.id}`} />
                <span className="text-[11.5px] text-[#8E8FA5]">{q.date}</span>
                <span className="ml-auto text-[12.5px] font-semibold text-[#1A1B2E]">{fmtEur(q.amount)}</span>
                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">{q.status || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Invoices */}
      <div className="soft-card p-5">
        <h4 className="text-[13.5px] font-semibold text-[#1A1B2E] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Factures ({k.invoices_count})</h4>
        {data.recent_invoices.length === 0 ? <div className="text-[12.5px] italic text-[#8E8FA5]">Aucune.</div> : (
          <div className="space-y-1.5">
            {data.recent_invoices.map((f) => (
              <div key={f.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE]">
                <Receipt className="w-3.5 h-3.5 text-[#0F766E]" />
                <span className="font-mono text-[12px] text-[#1A1B2E]">{f.id}</span>
                <span className="text-[11.5px] text-[#8E8FA5]">Éch. {f.dueDate || f.date}</span>
                <span className="ml-auto text-[12.5px] font-semibold text-[#1A1B2E]">{fmtEur(f.amount)}</span>
                <StatusBadge status={f.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== Pré-inscriptions / Conventions / Convocations ==================== */
function ListSection({ title, emptyMsg, items, sortKey, render, testId }) {
  const sorted = (items || []).slice().sort((a, b) => new Date(b[sortKey] || b.created_at || 0) - new Date(a[sortKey] || a.created_at || 0));
  return (
    <div className="soft-card p-5" data-testid={testId}>
      <h3 className="text-[15px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title} ({sorted.length})</h3>
      {sorted.length === 0 ? (
        <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center text-[12.5px] text-[#8E8FA5]">{emptyMsg}</div>
      ) : (
        <div className="space-y-2">{sorted.map(render)}</div>
      )}
    </div>
  );
}

function PreinscriptionsTab({ company, navigate }) {
  return (
    <ListSection
      testId="company-preinscriptions-list"
      title="Pré-inscriptions archivées"
      emptyMsg="Aucune pré-inscription pour cette entreprise. Une pré-inscription est archivée dès qu'une session est créée depuis un devis signé."
      items={company.preinscriptions}
      sortKey="created_at"
      render={(p) => (
        <div key={p.id} role="button" tabIndex={0} onClick={() => p.session_id && navigate(`/app/sessions?focus=${p.session_id}`)} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#C7D2FE] hover:bg-white cursor-pointer">
          <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"><CalendarPlus className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{p.session_title || p.session_id}</div>
            <div className="text-[11px] text-[#6B6C85] mt-0.5 flex flex-wrap gap-x-3">
              {p.startDate && <span>{fmtDate(p.startDate)}{p.endDate && p.endDate !== p.startDate ? ` → ${fmtDate(p.endDate)}` : ''}</span>}
              {p.learner_count != null && <span>{p.learner_count} apprenant{p.learner_count > 1 ? 's' : ''}</span>}
              {p.quote_id && <span>Devis {p.quote_id}</span>}
            </div>
          </div>
          <button onClick={async (e) => { e.stopPropagation(); try { const r = await api.get(`/preinscriptions/${p.id}/pdf`, { responseType: 'blob' }); const u = URL.createObjectURL(r.data); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); } catch { toast({ title: 'Erreur' }); } }} className="w-9 h-9 rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF] flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
        </div>
      )}
    />
  );
}

function ConventionsTab({ company, navigate }) {
  return (
    <ListSection
      testId="company-conventions-list"
      title="Conventions de formation signées"
      emptyMsg="Aucune convention signée pour cette entreprise. Les conventions sont archivées ici dès qu'elles sont signées par le client via le lien public."
      items={company.signed_conventions}
      sortKey="signed_at"
      render={(c) => (
        <div key={c.id} role="button" tabIndex={0} onClick={() => c.session_id && navigate(`/app/sessions?focus=${c.session_id}`)} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#A7F3D0] hover:bg-white cursor-pointer">
          <div className="w-10 h-10 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center"><FileText className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">Convention {c.session_id || c.id}</div>
            <div className="text-[11px] text-[#6B6C85] mt-0.5 flex flex-wrap gap-x-3">
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#0F766E]" /> Signée par <strong>{c.signatory_name}</strong></span>
              {c.signed_at && <span>{fmtDateTime(c.signed_at)}</span>}
            </div>
          </div>
          <button onClick={async (e) => { e.stopPropagation(); try { const r = await api.get(`/companies/${company.id}/signed-conventions/${c.id}/pdf`, { responseType: 'blob' }); const u = URL.createObjectURL(r.data); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); } catch { toast({ title: 'Erreur' }); } }} className="w-9 h-9 rounded-lg bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC] flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
        </div>
      )}
    />
  );
}

function ConvocationsTab({ company, navigate }) {
  return (
    <ListSection
      testId="company-convocations-list"
      title="Convocations envoyées"
      emptyMsg="Aucune convocation envoyée pour cette entreprise."
      items={company.sent_convocations}
      sortKey="sent_at"
      render={(c) => (
        <div key={c.id} role="button" tabIndex={0} onClick={() => c.session_id && navigate(`/app/sessions?focus=${c.session_id}`)} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#A7F3D0] hover:bg-white cursor-pointer">
          <div className="w-10 h-10 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center"><Send className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">Convocation {c.session_id || c.id}</div>
            <div className="text-[11px] text-[#6B6C85] mt-0.5 flex flex-wrap gap-x-3">
              <span>{c.sent_count || 0} apprenant{(c.sent_count || 0) > 1 ? 's' : ''}</span>
              {c.sent_at && <span>{fmtDateTime(c.sent_at)}</span>}
              {c.with_welcome_booklet && <span className="text-[#0F766E] font-semibold">+ livret d'accueil</span>}
            </div>
          </div>
          <button onClick={async (e) => { e.stopPropagation(); try { const r = await api.get(`/companies/${company.id}/sent-convocations/${c.id}/pdf`, { responseType: 'blob' }); const u = URL.createObjectURL(r.data); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); } catch { toast({ title: 'Erreur' }); } }} className="w-9 h-9 rounded-lg bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC] flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
        </div>
      )}
    />
  );
}
